import type { BoardState } from "../board/board";
import { allLies } from "../board/board";
import type { Category, LayerId, LieType } from "../board/types";
import { LAYER_ORDER } from "../board/types";
import type { Presence, VerseDef } from "../cards/types";

/**
 * Category meets lie type — tight so wrong Word is a real miss.
 * Faith ↔ Fear; Peace ↔ Despair only (not Fear).
 * Faith / Wisdom ↔ Doubt (persuasion, not classic step-down).
 * Dogma is never a type-meet — only the current shell's demanded category peels
 * (see applyDogmaHit). Confusion is weak to every Answer (any category meets).
 */
export function meetsLie(
  category: Category,
  lieType: LieType | undefined,
): boolean {
  if (!lieType) return false;
  if (lieType === "confusion") return true;
  if (lieType === "dogma") return false;
  if (category === "faith" && lieType === "fear") return true;
  if (category === "peace" && lieType === "despair") return true;
  if (
    (category === "faith" || category === "wisdom") &&
    lieType === "doubt"
  ) {
    return true;
  }
  return false;
}

/**
 * Levels stepped by a single-target Answer (snipe).
 *
 * Ladder (default base 1):
 * - soften: always base only (usually −1) — predictable Redeem setup
 * - Off-type: base only (usually 1)
 * - Meet (category or strongVs): +1 → usually 2
 * - Perfect aim (micro only): +1 → up to 3 (full silence of a III)
 *
 * broad/standard never get the aim→3 spike — they pay for coverage, not depth.
 * @param correctTarget Mightiest / Innermost / Discern aim (bonus only on micro)
 */
export function answerStepCount(
  def: Pick<
    VerseDef,
    "answerLevels" | "category" | "strongVs" | "snipeTier"
  >,
  lieType: LieType | undefined,
  correctTarget = false,
): number {
  const base = Math.max(1, def.answerLevels ?? 1);
  const tier = def.snipeTier ?? "standard";

  // Flat chip: no meet / aim bonuses (opens Redeem without over-answering)
  if (tier === "soften") {
    return base;
  }

  const listed =
    !!lieType && !!def.strongVs?.length && def.strongVs.includes(lieType);
  const meets = meetsLie(def.category, lieType) || listed;
  let steps = base;
  if (meets) steps += 1;

  if (correctTarget && tier === "micro") {
    steps += 1;
  }

  const cap = tier === "micro" ? 3 : 2;
  return Math.min(cap, steps);
}

/**
 * Drag hit the verse's preferred kind of target (bonus, not a gate).
 * - Mightiest: among highest-level lies on the board
 * - Innermost: on the deepest layer that still has a lie
 * - Discern: matches priority type when that type is present
 */
export function isCorrectSelectorTarget(
  def: Pick<VerseDef, "selector" | "priorityTypes">,
  target: { presence: Presence; layer: LayerId },
  board: BoardState,
): boolean {
  const sel = def.selector ?? "strongestLie";
  const lies = allLies(board);
  if (!lies.length) return false;

  if (sel === "strongestLie") {
    const maxLv = Math.max(...lies.map((l) => l.presence.level));
    return target.presence.level >= maxLv;
  }

  if (sel === "innermostLie") {
    let deepest = 0;
    for (const l of lies) {
      deepest = Math.max(deepest, LAYER_ORDER.indexOf(l.layer));
    }
    return LAYER_ORDER.indexOf(target.layer) === deepest;
  }

  if (sel === "typePriority" && def.priorityTypes?.length) {
    for (const t of def.priorityTypes) {
      const has = lies.some((l) => l.presence.lieType === t);
      if (!has) continue;
      return target.presence.lieType === t;
    }
    // No preferred type on board — any hit is "correct enough"
    return true;
  }

  return false;
}

/** Player-facing name for correct-target bonus log lines. */
export function correctTargetLabel(
  def: Pick<VerseDef, "selector" | "priorityTypes">,
): string | null {
  const sel = def.selector ?? "strongestLie";
  if (sel === "strongestLie") return "Aims strongest";
  if (sel === "innermostLie") return "Aims deepest";
  if (sel === "typePriority") {
    const types = def.priorityTypes ?? [];
    if (!types.length) return "Hunts preferred type";
    const names = types
      .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
      .join(" → ");
    return `Hunts ${names}`;
  }
  return null;
}

/** @deprecated Prefer meetsLie / answerStepCount — kept for any residual imports. */
export function matchupMul(
  category: Category,
  lieType: LieType | undefined,
): number {
  if (meetsLie(category, lieType)) return 1.5;
  if (category === "faith" && lieType === "despair") return 0.85;
  return 1;
}
